/** Memories photo looks + tones (CSS; originals stay untouched).
 *
 * Two independent layers:
 * 1) Look — eighties | film | polaroid | rustic | none
 * 2) Tone — bw | bronze | none  (can stack on any look, including Polaroid)
 *
 * Only Polaroid uses Instant card chrome. Film uses a 35mm strip frame.
 */

export const MEMORIES_LOOKS = [
  { id: 'eighties', labelKey: 'memories.filterEighties' },
  { id: 'film', labelKey: 'memories.filterFilm' },
  { id: 'polaroid', labelKey: 'memories.filterPolaroid' },
  { id: 'rustic', labelKey: 'memories.filterRustic' },
];

export const MEMORIES_TONES = [
  { id: 'bw', labelKey: 'memories.filterBw' },
  { id: 'bronze', labelKey: 'memories.filterBronze' },
];

/** @deprecated use MEMORIES_LOOKS — kept for older imports */
export const MEMORIES_VISUAL_FILTERS = MEMORIES_LOOKS;

export const MEMORIES_LOOK_IDS = MEMORIES_LOOKS.map((item) => item.id);
export const MEMORIES_TONE_IDS = MEMORIES_TONES.map((item) => item.id);
export const MEMORIES_VISUAL_FILTER_IDS = MEMORIES_LOOK_IDS;

export const MEMORIES_FILTER_COLORS = {
  eighties: { amber: '#E8A45A', rose: '#C45C4A', haze: '#D4C4A8', shadow: '#3A2A22' },
  film: { strip: '#F7F4EE', hole: '#1A1612', frame: '#111111', edge: '#C8C0B4' },
  polaroid: { frame: '#F5F0E6', chin: '#EDE6D9', fade: '#D4C4A8' },
  rustic: { cream: '#EDE4D4', warm: '#8B6914', stain: '#5C3D24' },
  bw: { black: '#111111' },
  bronze: { highlight: '#E8D9C0', mid: '#9A7A55', shadow: '#4A3220' },
};

/** Classic Instant / Polaroid: 3.5×4.2 outer, 1:1 photo window */
export const INSTANT_CARD = {
  outerAspectRatio: '35 / 42',
  photoAspectRatio: '1',
  /** Equal top/side border as % of card width; chin fills the rest */
  edgePct: 6,
  paper: '#F7F3EB',
  paperEdge: 'rgba(0,0,0,0.22)',
  caption: '#3D342C',
  mutedCaption: '#6A5E52',
};

/** White 35mm strip: landscape roll — half | full | half of the photo */
export const FILM_STRIP = {
  outerAspectRatio: '21 / 9',
  strip: '#F7F4EE',
  hole: '#1A1612',
  frameEdge: 'rgba(26,22,18,0.35)',
  caption: '#3A342C',
  /** Top/bottom clearance for sprocket rows (keep tight) */
  sprocketPadPct: 7,
  /** Near-flush sides so end frames read as cut by the strip edge */
  sidePadPct: 1.5,
  /** Gap between frames as % of strip width */
  frameGapPct: 3.5,
};

/**
 * Exact aged print matte extracted from a real vintage photo.
 * User photo fills the inset; overlay PNG supplies border + emulsion damage.
 */
export const RUSTIC_PRINT = {
  outerAspectRatio: '756 / 1024',
  overlayUrl: '/brand/memories/rustic-print-overlay.png',
  inset: {
    left: '7.41%',
    top: '6.45%',
    right: '7.41%',
    bottom: '8.5%',
  },
  caption: '#4A3A2A',
};

export function normalizeVisualLook(value) {
  if (value == null || value === '' || value === 'none') return null;
  const id = String(value).trim().toLowerCase();
  // Migrate legacy tones that were stored as visual_filter
  if (id === 'bw' || id === 'bronze') return null;
  return MEMORIES_LOOK_IDS.includes(id) ? id : null;
}

export function normalizeVisualTone(value) {
  if (value == null || value === '' || value === 'none') return null;
  const id = String(value).trim().toLowerCase();
  return MEMORIES_TONE_IDS.includes(id) ? id : null;
}

/** @deprecated use normalizeVisualLook */
export function normalizeVisualFilter(value) {
  const look = normalizeVisualLook(value);
  if (look) return look;
  // If someone passes a tone as filter, ignore for look id
  return null;
}

export function getVisualFilterMeta(filterId) {
  const look = normalizeVisualLook(filterId);
  if (look) return MEMORIES_LOOKS.find((item) => item.id === look) || null;
  const tone = normalizeVisualTone(filterId);
  if (tone) return MEMORIES_TONES.find((item) => item.id === tone) || null;
  return null;
}

function instantCardFrame(paper = INSTANT_CARD.paper) {
  return {
    backgroundColor: paper,
    boxShadow: [
      '0 10px 28px rgba(0,0,0,0.28)',
      '0 2px 6px rgba(0,0,0,0.12)',
      `0 0 0 1px ${INSTANT_CARD.paperEdge}`,
      'inset 0 0 0 1px rgba(255,255,255,0.7)',
    ].join(', '),
    border: `1px solid ${INSTANT_CARD.paperEdge}`,
    borderRadius: '2px',
  };
}

function filmStripFrame() {
  const { strip, hole } = FILM_STRIP;
  // Dark rectangular sprocket holes on white strip — close to edges
  const sprocketRow =
    `repeating-linear-gradient(90deg, transparent 0 5px, ${hole} 5px 14px, transparent 14px 22px)`;
  return {
    backgroundColor: strip,
    backgroundImage: [
      sprocketRow,
      sprocketRow,
      `linear-gradient(180deg, ${strip}, ${strip})`,
    ].join(', '),
    backgroundSize: '100% 8px, 100% 8px, 100% 100%',
    backgroundPosition: 'left 3px top 3px, left 3px bottom 3px, center',
    backgroundRepeat: 'repeat-x, repeat-x, no-repeat',
    boxShadow: '0 10px 28px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.12)',
    border: '1px solid rgba(0,0,0,0.18)',
    borderRadius: '2px',
  };
}

const GRAIN =
  'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.55\'/%3E%3C/svg%3E")';

const DUST =
  'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 300 300\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'40\' cy=\'50\' r=\'1.2\' fill=\'%23fff\' opacity=\'0.55\'/%3E%3Ccircle cx=\'120\' cy=\'30\' r=\'0.8\' fill=\'%23fff\' opacity=\'0.4\'/%3E%3Ccircle cx=\'200\' cy=\'70\' r=\'1\' fill=\'%23fff\' opacity=\'0.45\'/%3E%3Ccircle cx=\'260\' cy=\'40\' r=\'0.7\' fill=\'%23fff\' opacity=\'0.35\'/%3E%3Ccircle cx=\'80\' cy=\'110\' r=\'0.6\' fill=\'%23fff\' opacity=\'0.3\'/%3E%3C/svg%3E")';

function combineCssFilters(...parts) {
  return parts.filter(Boolean).join(' ');
}

function lookLayer(lookId) {
  const colors = MEMORIES_FILTER_COLORS[lookId] || {};

  if (lookId === 'eighties') {
    // Warm late-80s / early-90s print: golden skin, muted greens, soft grain — not neon
    return {
      imageFilter: 'sepia(0.18) saturate(1.12) contrast(0.94) brightness(0.96)',
      overlaySx: {
        backgroundImage: [
          GRAIN,
          `linear-gradient(165deg, ${colors.amber}33 0%, transparent 38%, ${colors.rose}28 72%, ${colors.shadow}44 100%)`,
          `linear-gradient(180deg, ${colors.haze}40 0%, transparent 35%, rgba(40,28,22,0.22) 100%)`,
          'radial-gradient(ellipse at center, transparent 50%, rgba(35,24,18,0.35) 100%)',
        ].join(', '),
        backgroundSize: '100px 100px, cover, cover, cover',
        mixBlendMode: 'soft-light',
        opacity: 0.85,
      },
      frame: null,
    };
  }

  if (lookId === 'film') {
    return {
      imageFilter: 'saturate(0.75) contrast(1.08) brightness(1.02) sepia(0.08)',
      overlaySx: {
        backgroundImage: [
          GRAIN,
          DUST,
          'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.35) 100%)',
        ].join(', '),
        backgroundSize: '140px 140px, cover, cover',
        mixBlendMode: 'soft-light',
        opacity: 0.65,
      },
      frame: 'film',
    };
  }

  if (lookId === 'polaroid') {
    return {
      imageFilter: 'saturate(0.78) contrast(0.92) brightness(1.05) sepia(0.12)',
      overlaySx: {
        backgroundImage: [
          DUST,
          GRAIN,
          `linear-gradient(180deg, ${colors.fade}40, transparent 40%, rgba(50,40,30,0.16))`,
          'radial-gradient(ellipse at center, transparent 55%, rgba(30,25,20,0.28) 100%)',
        ].join(', '),
        backgroundSize: 'cover, 180px 180px, cover, cover',
        mixBlendMode: 'soft-light',
        opacity: 0.7,
      },
      frame: 'polaroid',
    };
  }

  if (lookId === 'rustic') {
    // Real vintage print matte (border + cracks from photo); mild grade on the photo only
    return {
      imageFilter: 'grayscale(0.55) sepia(0.55) contrast(0.9) brightness(1.02) saturate(0.8)',
      overlaySx: null,
      frame: 'rustic',
    };
  }

  return { imageFilter: '', overlaySx: null, frame: null };
}

function toneLayer(toneId) {
  const colors = MEMORIES_FILTER_COLORS[toneId] || {};
  if (toneId === 'bw') {
    return {
      imageFilter: 'grayscale(1) contrast(1.12) brightness(1.02)',
      overlaySx: {
        background: `radial-gradient(ellipse at center, transparent 55%, ${colors.black}55 100%)`,
        mixBlendMode: 'multiply',
        opacity: 0.35,
      },
    };
  }
  if (toneId === 'bronze') {
    // Dusty copper-bronze grade (no fake crack lines — rustic print supplies damage)
    return {
      imageFilter: 'grayscale(1) sepia(0.85) hue-rotate(-8deg) saturate(0.95) contrast(0.9) brightness(1.0)',
      overlaySx: {
        backgroundImage: [
          GRAIN,
          `linear-gradient(165deg, ${colors.highlight}40 0%, transparent 42%, ${colors.mid}30 70%, ${colors.shadow}55 100%)`,
          `radial-gradient(ellipse at center, transparent 48%, ${colors.shadow}60 100%)`,
        ].join(', '),
        backgroundSize: '110px 110px, cover, cover',
        mixBlendMode: 'soft-light',
        opacity: 0.75,
      },
    };
  }
  return { imageFilter: '', overlaySx: null };
}

/**
 * Compose look + tone presentation.
 * @param {string|null} lookId
 * @param {string|null} toneId
 */
export function getVisualFilterPresentation(lookId, toneId = null) {
  // Back-compat: getVisualFilterPresentation('polaroid') or getVisualFilterPresentation(frame)
  let look = lookId;
  let tone = toneId;
  if (look && typeof look === 'object') {
    tone = look.visual_tone ?? look.tone ?? tone;
    look = look.visual_filter ?? look.look ?? null;
  }

  // Legacy: tone stored in visual_filter column
  if (!normalizeVisualLook(look) && normalizeVisualTone(look) && !tone) {
    tone = look;
    look = null;
  }

  look = normalizeVisualLook(look);
  tone = normalizeVisualTone(tone);

  const empty = {
    usesCard: false,
    usesFilmStrip: false,
    usesRusticPrint: false,
    squareCrop: false,
    imageSx: {},
    frameSx: {},
    overlaySx: null,
    toneOverlaySx: null,
    captionColor: '',
    badgeColor: '',
    look,
    tone,
  };

  if (!look && !tone) return empty;

  const lookPart = lookLayer(look);
  const tonePart = toneLayer(tone);

  const presentation = {
    ...empty,
    look,
    tone,
    imageSx: {
      filter: combineCssFilters(lookPart.imageFilter, tonePart.imageFilter) || undefined,
    },
    overlaySx: lookPart.overlaySx,
    toneOverlaySx: tonePart.overlaySx,
  };

  if (lookPart.frame === 'polaroid') {
    presentation.usesCard = true;
    presentation.squareCrop = true;
    presentation.outerAspectRatio = INSTANT_CARD.outerAspectRatio;
    presentation.frameSx = instantCardFrame(MEMORIES_FILTER_COLORS.polaroid.frame);
    presentation.captionColor = INSTANT_CARD.caption;
    presentation.badgeColor = INSTANT_CARD.mutedCaption;
  }

  if (lookPart.frame === 'film') {
    presentation.usesFilmStrip = true;
    presentation.squareCrop = false;
    presentation.outerAspectRatio = FILM_STRIP.outerAspectRatio;
    presentation.frameSx = filmStripFrame();
    presentation.captionColor = FILM_STRIP.caption;
    presentation.badgeColor = FILM_STRIP.caption;
  }

  if (lookPart.frame === 'rustic') {
    presentation.usesRusticPrint = true;
    presentation.squareCrop = false;
    presentation.outerAspectRatio = RUSTIC_PRINT.outerAspectRatio;
    presentation.frameSx = {
      backgroundColor: '#E8DFD0',
      boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
      borderRadius: '2px',
    };
    presentation.overlayUrl = RUSTIC_PRINT.overlayUrl;
    presentation.inset = RUSTIC_PRINT.inset;
    presentation.captionColor = RUSTIC_PRINT.caption;
    presentation.badgeColor = RUSTIC_PRINT.caption;
  }

  return presentation;
}

export function describeLookTone(lookId, toneId, t) {
  const look = normalizeVisualLook(lookId);
  const tone = normalizeVisualTone(toneId);
  const parts = [];
  if (look) {
    const meta = MEMORIES_LOOKS.find((item) => item.id === look);
    if (meta) parts.push(t(meta.labelKey));
  }
  if (tone) {
    const meta = MEMORIES_TONES.find((item) => item.id === tone);
    if (meta) parts.push(t(meta.labelKey));
  }
  return parts.join(' · ');
}
